import { useMemo, useState } from "react";
import { MdOutlineCurrencyRupee } from "react-icons/md";
import ModalElement from "../../../Common/ModalElement"
import InputElement from "../../../Common/InputElement";
import ButtonElement from "../../../Common/ButtonElement";
import { FaPlus, FaX } from "react-icons/fa6";
import { validateFields, handleDownload, formatDate, validateInput, parseNumber } from "../../../utils";
import { appApis, dropdownDefaultList, eSign, isInovize } from "../../../constants";
import SearchableDropdown from "../../../Common/SearchDropdown";
import moment from "moment";
import { TfiReceipt } from "react-icons/tfi";
import { useApiRequest } from "../../../hooks/useApiRequest";

const GST_PERCENTAGE = 18;
const itemsObj = {
    inventory: "",
    quantity: "",
    perPeiceAmount: "",
    description: "",
    inventoryErr: null,
    quantityErr: null,
    perPeiceAmountErr: null,
    descriptionErr: null
}

const QuotationGenerate = ({ show, hideModal, onSuccess, billingCompanies, inventoryTypes }) => {
    const [details, setDetails] = useState({
        company: "",
        orderDate: moment(),
        quotationNo: "",
        quotatedCompany: "",
        gstType: "GST",
        gstNo: "",
        currentIndex: 0,
        items: [{ ...itemsObj }],
        gstNoErr: null,
        quotationNoErr: null,
        quotatedCompanyErr: null,
        companyErr: null,
        orderDateErr: null,
    });
    const { request: submitRequest, loading: submitLoading } = useApiRequest();

    const { company, orderDate, companyErr, orderDateErr, quotationNo, quotationNoErr, quotatedCompany, quotatedCompanyErr, gstType, gstNo, gstNoErr, items, currentIndex } = details;

    const onChangeHandler = (event, itemIndex = null) => {
        const { name, value } = event.target;
        if (itemIndex != null) {
            const temp = [...items];
            temp[itemIndex][name] = value;
            temp[itemIndex][`${name}Err`] = null;
            setDetails((prev) => ({ ...prev, items: [...temp] }));
        } else {
            setDetails((prev) => ({ ...prev, [name]: value, [`${name}Err`]: null }))
        }
    }

    const onSelectHandler = (event, isItem) => {
        const { name, value } = event.target;
        if (isItem) {
            const temp = [...items];
            temp[currentIndex][name] = value?.name ?? "";
            temp[currentIndex][`${name}Id`] = value?.id ?? "";
            temp[currentIndex][`${name}Err`] = null;
            setDetails((prev) => ({ ...prev, items: [...temp] }));
        } else {
            setDetails((prev) => ({ ...prev, [name]: value?.name ?? "", [`${name}Id`]: value?.id ?? "", [`${name}Err`]: null }))
        }
    }

    const quotationAmount = useMemo(() => {
        return items.reduce((acc, curr) => {
            const amount = (acc.amount || 0) + (curr?.quantity || 0) * (curr?.perPeiceAmount || 0);
            const gst = amount * (gstType == "GST" ? (GST_PERCENTAGE / 100) : 0);
            const total = (amount + gst);
            return { gst, amount, total }
        }, { gst: 0, amount: 0, total: 0 })
    }, [items, currentIndex, gstType]);

    const validateItem = () => {
        const { inventory, quantity, perPeiceAmount, description } = items[currentIndex];
        const inventoryErr = validateInput(inventory, "text");
        const quantityErr = validateInput(quantity, "number");
        const perPeiceAmountErr = validateInput(perPeiceAmount, "number");
        const descriptionErr = validateInput(description, "text");
        if (inventoryErr || quantityErr || perPeiceAmountErr || descriptionErr || !parseNumber(quantity)) {
            const temp = [...items];
            const obj = temp[currentIndex];
            temp[currentIndex] = { ...obj, inventoryErr, descriptionErr, quantityErr: quantityErr ? quantityErr : parseNumber(quantity) ? null : "Quantity must be greater than one!!!", perPeiceAmountErr };
            return temp;
        }
        return null;
    }

    const addItems = () => {
        const errorObj = validateItem();
        if (errorObj?.length) {
            const temp = [...errorObj];
            setDetails(prev => ({ ...prev, items: [...temp] }))
        } else {
            setDetails(prev => ({ ...prev, items: [...items, { ...itemsObj }], currentIndex: currentIndex + 1 }))
        }
    }

    const removeItem = (index) => {
        if (currentIndex > 0) {
            const temp = items.filter((_, idx) => index != idx);
            setDetails(prev => ({ ...prev, items: [...temp], currentIndex: temp.length }))
        }
    }

    const submit = () => {
        validateFields(
            {
                quotationNoErr: [quotationNo, "text"],
                orderDateErr: [orderDate, "date"],
                companyErr: [company, "text"],
                quotatedCompanyErr: [quotatedCompany, "text"]
            },
            setDetails,
            () => {
                const errorObj = validateItem();
                if (errorObj?.length) {
                    const temp = [...errorObj];
                    setDetails(prev => ({ ...prev, items: [...temp] }))
                } else {
                    const fromCompany = billingCompanies?.find(item => item?.name == company);
                    const devices = items.map(item => ([{
                        quantity: parseNumber(item?.quantity ?? 0),
                        description: item?.description ?? "",
                        amount: parseNumber(item?.perPeiceAmount ?? 0)
                    }]));
                    const stamp = isInovize ? fromCompany?.companyStampUrl : eSign[String(fromCompany?.name ?? "").replaceAll(" ", "").toLowerCase() || "sbtracking"];

                    const invoiceDetails = {
                        fromCompany: {
                            name: fromCompany?.name ?? "",
                            address: fromCompany?.address ?? "",
                            gst: fromCompany?.gstNumber ?? "",
                            email: fromCompany?.email ?? "",
                            phoneNo: fromCompany?.mobileNo ?? "",
                            bankName: fromCompany?.bankName ?? "",
                            branch: fromCompany?.branchName ?? "",
                            accountNo: fromCompany?.bankAccountNo ?? "",
                            ifscCode: fromCompany?.ifscCode ?? "",
                            sacCode: isInovize ? fromCompany?.sacCode ?? "" : "998313",
                            hsnCode: isInovize ? fromCompany?.hsnCode ?? "" : "852691",
                            stampUrl: stamp,
                            digitalSignatureUrl: isInovize ? fromCompany?.digitalSignatureUrl ?? "" : null
                        },
                        toCompany: {
                            name: quotatedCompany ?? "",
                            gst: gstType == "GST" ? gstNo ?? "" : "",
                        },
                        invoiceNo: quotationNo,
                        date: formatDate(orderDate),
                        vehicles: devices || [],
                        amount: quotationAmount.amount ?? 0,
                        gstAmount: quotationAmount.gst ?? 0,
                        isComboCase: false,
                        gstPercentage: gstType == "GST" ? GST_PERCENTAGE : 0
                    }
                    const payload = {
                        "companyId": fromCompany?.id ?? 0,
                        "quotationNo": quotationNo,
                        "quotationDate": invoiceDetails.date,
                        "quotatedCompany": quotatedCompany,
                        "gstType": gstType,
                        "gstNo": gstType == "GST" ? gstNo || "" : "",
                        "items": items.map(item => {
                            const perPeice = parseNumber(item?.perPeiceAmount ?? 0);
                            const quantity = parseNumber(item?.quantity ?? 0);
                            const billingAmount = perPeice * quantity;
                            const gstAmount = billingAmount * (gstType == "GST" ? (GST_PERCENTAGE / 100) : 0);
                            const totalAmount = billingAmount + gstAmount;

                            return {
                                "inventory_type_id": parseNumber(item?.inventoryId ?? 0),
                                "quantity": quantity,
                                "description": item?.description ?? "",
                                "per_piece_amount": perPeice,
                                "billing_amount": billingAmount,
                                "gst_amount": gstAmount,
                                "total_amount": totalAmount
                            }
                        })
                    }
                    submitRequest({
                        method: "POST",
                        url: appApis.generateQuotation,
                        body: payload,
                        onSuccess: () => {
                            handleDownload(invoiceDetails, false, true);
                            setTimeout(() => {
                                onSuccess();
                                hideModal();
                            }, 400)
                        }
                    })
                }
            }
        )
    }

    return <ModalElement height={"100vh"} FooterComponent={<div className="submit-btn text-center my-3">
        <ButtonElement text={"Submit"} cssClass="small-radius mb-1" onClick={submit} variant="btn-outline-primary" loading={submitLoading} />
    </div>} size="xl" show={show} title={"Quotation"} onHide={hideModal}>
        <div className="new-order mb-3">
            <h5 className="mb-3">Quotation Details</h5>
            <div className="row">
                <div className="col-lg-6 mb-2">
                    <InputElement
                        label={"Quotation Number"}
                        placeholder={"Enter Quotation Number"}
                        name={"quotationNo"}
                        value={quotationNo}
                        error={quotationNoErr}
                        onChange={onChangeHandler}
                    />
                </div>
                <div className="col-lg-6 mb-2">
                    <InputElement
                        type="date"
                        label={"Order Date"}
                        placeholder={"Enter Order Date"}
                        name={"orderDate"}
                        value={orderDate}
                        error={orderDateErr}
                        onChange={onChangeHandler}
                    />
                </div>
                <div className="col-12 mb-2">
                    <SearchableDropdown
                        label={"Billing Company Name"}
                        placeholder={"Select Billing Company Name"}
                        value={company}
                        error={companyErr}
                        options={billingCompanies}
                        onSelect={(value) => onSelectHandler({ target: { name: "company", value } })}
                    />
                </div>
                <div className={`col-lg-${gstType == "GST" ? "4" : "6"} col-md-6 mb-2`}>
                    <InputElement
                        label={"Quotated Company"}
                        placeholder={"Enter Quotated Company"}
                        name={"quotatedCompany"}
                        value={quotatedCompany}
                        error={quotatedCompanyErr}
                        onChange={onChangeHandler}
                    />
                </div>
                <div className={`col-lg-${gstType == "GST" ? "4" : "6"} col-md-6 mb-2`}>
                    <SearchableDropdown
                        label={"GST type"}
                        placeholder={"Select GST Type"}
                        value={gstType}
                        error={false}
                        options={dropdownDefaultList.gstStatus}
                        onSelect={(value) => onSelectHandler({ target: { name: "gstType", value } })}
                    />
                </div>
                {gstType == "GST" ? <div className="col-lg-4 col-md-6 mb-2">
                    <InputElement
                        isRequired={false}
                        label={"GST Number"}
                        placeholder={"Enter GST Number"}
                        name={"gstNo"}
                        value={gstNo}
                        error={false}
                        icon={<TfiReceipt size={20} />}
                        onChange={onChangeHandler}
                    />
                </div> : null}
                <div className="mb-2">
                    <h5 className="mb-2">Item Details</h5>
                    {items.map((item, index) => {
                        const isCompleted = currentIndex != index;
                        const { inventory, inventoryErr, perPeiceAmount, perPeiceAmountErr, quantity, quantityErr, description, descriptionErr } = item;
                        return <div className="row justify-content-center align-items-center" key={index}>
                            <div className="col-lg-11">
                                <div className="row">
                                    <div className="col-lg-3 mb-2">
                                        <SearchableDropdown
                                            label={"Inventory Type"}
                                            placeholder={"Enter Inventory Type"}
                                            value={inventory}
                                            error={inventoryErr}
                                            options={inventoryTypes}
                                            onSelect={(value) => onSelectHandler({ target: { name: "inventory", value } }, true)} />
                                    </div>
                                    <div className="col-lg-2 mb-2">
                                        <InputElement
                                            type="number"
                                            label={"Per pc Amt"}
                                            placeholder={"Amount"}
                                            name={"perPeiceAmount"}
                                            value={perPeiceAmount}
                                            error={perPeiceAmountErr}
                                            onChange={(event) => onChangeHandler(event, index)}
                                        />
                                    </div>
                                    <div className="col-lg-2 mb-2">
                                        <InputElement
                                            type="number"
                                            label={"Quantity"}
                                            placeholder={"Quantity"}
                                            name={"quantity"}
                                            value={quantity}
                                            error={quantityErr}
                                            onChange={(event) => onChangeHandler(event, index)}
                                        />
                                    </div>
                                    <div className="col-lg-5 mb-2">
                                        <InputElement
                                            label={"Description"}
                                            placeholder={"Enter description"}
                                            name={"description"}
                                            value={description}
                                            error={descriptionErr}
                                            onChange={(event) => onChangeHandler(event, index)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-1 text-center">
                                <ButtonElement text={isCompleted ? <FaX size={"16px"} /> : <FaPlus size={"16px"} />} cssClass="small-radius plus-btn" onClick={isCompleted ? () => removeItem(index) : addItems} variant="btn-outline-primary" />
                            </div>
                        </div>
                    })}
                </div>
                <div className={`col-lg-${gstType == "GST" ? "4" : "6"} col-md-6 mb-2`}>
                    <InputElement
                        disable={true}
                        label={"Amount"}
                        placeholder={"Enter Amount"}
                        name={"amount"}
                        value={quotationAmount.amount}
                        error={false}
                        icon={<MdOutlineCurrencyRupee size={20} />}
                        onChange={onChangeHandler}
                    />
                </div>
                {gstType == "GST" ? <div className={`col-lg-4 col-md-6 mb-2`}>
                    <InputElement
                        disable={true}
                        label={"GST Amount"}
                        placeholder={"Enter GST Amount"}
                        name={"gst"}
                        value={quotationAmount.gst}
                        error={false}
                        icon={<MdOutlineCurrencyRupee size={20} />}
                        onChange={onChangeHandler}
                    />
                </div> : null}
                <div className={`col-lg-${gstType == "GST" ? "4" : "6"} col-md-6 mb-2`}>
                    <InputElement
                        disable={true}
                        label={"Total Amount"}
                        placeholder={"Enter Total Amount"}
                        name={"total"}
                        value={quotationAmount.total}
                        error={false}
                        icon={<MdOutlineCurrencyRupee size={20} />}
                        onChange={onChangeHandler}
                    />
                </div>
            </div>
        </div>
    </ModalElement>
}

export default QuotationGenerate;